import { useState } from "react";
import { Stage, Btn, Check, MiniBar } from "../components/primitives";

// Kit 2026: dourado é destaque único por tela; aqui o acento é o navy e as faixas escuras usam o palco.
const GOLD = "var(--primary)";
const DARK = "var(--stage)";

const PROMPT_RC2CLICKS_DESIGN_SYSTEM = `Quero evoluir o sistema visual da RC2CLICKS para um ecossistema editorial premium completo.

A RC2CLICKS deve transmitir:
- inteligência operacional
- aquisição estruturada
- performance analítica
- clareza estratégica
- sofisticação tecnológica
- autoridade silenciosa
- sistemas de crescimento

Hoje a identidade base possui:
- preto premium
- dourado sofisticado
- JetBrains Mono
- estética operacional
- aparência analítica

Agora quero expandir isso para um sistema híbrido com:
- variação escura premium
- variação clara editorial
- uso estratégico de fotografia
- composições com dados
- estética humana sofisticada

Crie 4 direções visuais oficiais:

DIREÇÃO 01 — Operational Editorial
DIREÇÃO 02 — Clean Intelligence
DIREÇÃO 03 — Human Performance
DIREÇÃO 04 — Strategic Data

Para cada direção, crie:
- reels cover
- story
- post de autoridade
- carrossel
- CTA premium
- regras de uso

Evitar:
- aparência genérica de agência
- excesso visual
- neon
- gradientes exagerados
- marketing agressivo
- visual motivacional

Quero um sistema visual digno de uma empresa de inteligência operacional.`;

const PROMPT_SERIE_EPISODIOS = `Crie uma coleção editorial premium para a série RC2CLICKS Episódios 01–06 usando o Sistema de Design RC2CLICKS.

Objetivo:
Construir uma sequência visual com sensação de inteligência operacional, autoridade estratégica e posicionamento premium.

Estética:
- editorial Bloomberg / Financial Times
- terminal intelligence
- minimalismo premium
- grids sofisticados
- preto dominante com dourado editorial
- algumas variações em fundo branco premium
- visual humano, não aparência de template

Criar:
1. 6 capas de reels 1080x1920
2. 6 stories de continuidade
3. 2 posts manifesto
4. 1 carrossel premium de abertura da série

Episódios:

EP.01 — O erro que a maioria das agências comete com médicos
EP.02 — A dor real do ortopedista no digital
EP.03 — Presença digital não significa autoridade
EP.04 — Tráfego sem estratégia desperdiça verba
EP.05 — A Estrutura de Aquisição RC2CLICKS
EP.06 — O que muda quando a estrutura é feita certa

Direções:
- usar principalmente Operational Editorial e Strategic Data
- inserir Human Performance em peças mais emocionais
- usar Clean Intelligence nas versões claras

Adicionar microdetalhes editoriais:
- códigos
- indicadores
- labels
- rodapés
- paginação
- sinais gráficos discretos
- estrutura de revista premium

Sem visual genérico de social media.
Quero sensação de marca estratégica.`;

const PROMPT_CLIENTE_FUNDACAO = `Crie a fundação visual premium para [NOME DO CLIENTE].

Contexto:
[Explique quem é o cliente, nicho, público, posicionamento e serviço principal.]

A identidade deve transmitir:
- autoridade
- confiança
- sofisticação
- clareza
- posicionamento premium
- diferenciação
- consistência visual

Evitar:
- aparência genérica
- excesso visual
- estética popular
- excesso de elementos
- visual de template pronto

Direção visual desejada:
[Descreva cores, sensação, referências e estilo.]

Crie 4 direções visuais diferentes.

Cada direção deve conter:
- capa de reels
- story
- post de autoridade
- carrossel
- CTA
- regras visuais

O objetivo é criar um sistema visual premium, consistente e flexível para redes sociais.`;

const PROMPT_CLIENTE_BRANDBOOK = `Transforme as 4 direções visuais em um Brand Book editorial completo para [NOME DO CLIENTE].

Inclua:
- capa
- manifesto visual
- explicação das 4 direções
- quando usar cada direção
- paleta de cores
- tipografia
- regras de fundo
- regras de fotografia
- composição de posts
- composição de stories
- composição de reels
- composição de carrosséis
- o que fazer
- o que evitar
- assinatura visual da marca

O resultado deve funcionar como manual visual interno da marca.`;

const PROMPT_CLIENTE_PRODUCAO = `Crie uma coleção de conteúdos para [NOME DO CLIENTE] usando o sistema visual aprovado.

Criar:
- 6 capas de reels
- 6 stories
- 3 posts de autoridade
- 1 carrossel premium

Tema da coleção:
[TEMA]

Manter:
- identidade visual aprovada
- sofisticação
- consistência
- variação sem perder padrão
- leitura rápida no mobile

Evitar:
- visual genérico
- excesso de informação
- repetição excessiva`;

const CHECKLIST = [
  "Parece da marca?",
  "Leitura boa no celular?",
  "Contraste forte?",
  "Texto curto?",
  "Visual premium?",
  "Não está poluído?",
  "Headline tem força?",
  "CTA está discreto?",
  "Existe consistência com o sistema?",
  "Parece conteúdo estratégico, não arte solta?",
];

function CopyPrompt({ label, text, color = GOLD }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 6,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--faint)" }}>
          {label}
        </span>
        <Btn sm variant={copied ? "success" : "secondary"} onClick={copy}>{copied ? "✓ Copiado" : "Copiar prompt"}</Btn>
      </div>
      <div style={{
        background: "var(--surface-2)", borderRadius: 12,
        padding: "14px 16px", fontFamily: "var(--font-data)",
        fontSize: 12, color: "var(--ink)", lineHeight: 1.7,
        border: "1px solid var(--line)",
        whiteSpace: "pre-wrap", maxHeight: 260, overflowY: "auto",
      }}>
        {text}
      </div>
    </div>
  );
}

function Section({ title, icon, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{
      background: "var(--surface)", border: "1px solid var(--line)",
      borderRadius: "var(--r-card)", overflow: "hidden",
    }}>
      <div
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px", cursor: "pointer",
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((p) => !p); } }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 12,
            background: "var(--primary-soft)", display: "flex",
            alignItems: "center", justifyContent: "center",
            fontSize: 16, color: GOLD, flexShrink: 0,
          }}>
            {icon}
          </div>
          <div>
            <div style={{ font: "600 15px var(--font-display)", color: "var(--ink)" }}>{title}</div>
            {badge && <span className="eyebrow">{badge}</span>}
          </div>
        </div>
        <span className="chev" aria-hidden="true" style={{ color: "var(--faint)", transform: open ? "rotate(90deg)" : "none", transition: "transform var(--d) var(--ease)" }}>›</span>
      </div>

      {open && (
        <div style={{ padding: "0 22px 22px", borderTop: `1px solid ${"var(--line-soft)"}` }}>
          <div style={{ paddingTop: 18 }}>{children}</div>
        </div>
      )}
    </div>
  );
}

function Step({ num, title, children }) {
  return (
    <div style={{
      display: "flex", gap: 14, marginBottom: 20,
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: "50%",
        background: `${GOLD}20`, color: GOLD,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 1,
      }}>
        {num}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".05em", flexShrink: 0 }}>
        {label}
      </span>
      <span style={{ fontSize: 13, color: "var(--muted)" }}>{value}</span>
    </div>
  );
}

function Tag({ label, color = GOLD }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
      background: `${color}15`, color, display: "inline-block", marginRight: 6, marginBottom: 4,
    }}>
      {label}
    </span>
  );
}

export function SocialMediaView() {
  const [checkedItems, setCheckedItems] = useState({});

  function toggleCheck(i) {
    setCheckedItems((p) => ({ ...p, [i]: !p[i] }));
  }

  const allChecked = CHECKLIST.every((_, i) => checkedItems[i]);
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;

  return (
    <div className="view" style={{ maxWidth: 1000 }}>

      {/* Palco */}
      <Stage
        path="social-media"
        title="Sistema visual de"
        hl="social media"
        lead={
          <div style={{ display: "grid", gap: 8 }}>
            <span className="eyebrow" style={{ color: "var(--stage-muted)" }}>Claude Design + Canva</span>
            <p style={{ margin: 0 }}>
            Centraliza o processo de criação visual da RC2CLICKS e dos clientes usando Claude Design + Canva.
            </p>
          </div>
        }
      />

      {/* Lógica central */}
      <div style={{
        background: DARK, borderRadius: "var(--r-card)",
        padding: "20px 24px", marginBottom: 28,
        display: "flex", gap: 24, flexWrap: "wrap",
      }}>
        {[
          { icon: "◈", label: "Claude Design", desc: "Cria direção visual" },
          { icon: "→", label: "", desc: "" },
          { icon: "◻", label: "Canva", desc: "Executa e edita no dia a dia" },
          { icon: "→", label: "", desc: "" },
          { icon: "⬡", label: "App", desc: "Organiza o processo e os prompts" },
        ].map((item, i) =>
          item.label === "" ? (
            <div key={i} style={{ color: "var(--sidebar-faint)", fontSize: 20, display: "flex", alignItems: "center" }}>→</div>
          ) : (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: "rgb(255 255 255 / .08)", display: "flex",
                alignItems: "center", justifyContent: "center",
                fontSize: 15, color: "var(--stage-ink)",
              }}>
                {item.icon}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--stage-ink)" }}>{item.label}</div>
                <div style={{ fontSize: 11, color: "var(--stage-muted)" }}>{item.desc}</div>
              </div>
            </div>
          )
        )}
      </div>

      {/* Seções */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

        {/* 1. Identidade RC2CLICKS */}
        <Section title="Identidade da RC2CLICKS" icon="⬡" badge="Marca própria" defaultOpen>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>
              Quando usar esse fluxo
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {["posts", "stories", "capas de reels", "carrosséis", "campanhas", "séries", "materiais institucionais", "variações visuais da marca"].map((tag) => (
                <Tag key={tag} label={tag} />
              ))}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{
              background: "var(--line-soft)", borderRadius: 8,
              padding: "14px 16px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>
                Onde trabalhar
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{
                    flexShrink: 0, marginTop: 2, width: 8, height: 8, borderRadius: "50%",
                    background: GOLD,
                  }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>RC2CLICKS Design System</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>
                      Para alterar a identidade oficial — paleta, tipografia, padrões visuais estruturais.
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  <div style={{
                    flexShrink: 0, marginTop: 2, width: 8, height: 8, borderRadius: "50%",
                    background: "var(--cat-violet)",
                  }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>RC2CLICKS — Exploração Editorial</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>
                      Para criar peças, campanhas, séries e variações de produção real.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* 2. Design System */}
        <Section title="Evoluir o Design System da RC2CLICKS" icon="◈" badge="Mudanças estruturais">
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>
            Use apenas quando quiser mudar algo estrutural: fundo branco premium, nova direção visual, paleta, tipografia, novo padrão oficial, linguagem visual da marca.
          </p>
          <p style={{ margin: "0 0 4px", fontSize: 12, color: "var(--faint)", lineHeight: 1.5 }}>
            Cole no projeto <strong style={{ color: "var(--ink)" }}>RC2CLICKS Design System</strong> no Claude Design.
          </p>
          <CopyPrompt label="Prompt — Evoluir Design System" text={PROMPT_RC2CLICKS_DESIGN_SYSTEM} />
        </Section>

        {/* 3. Exploração Editorial */}
        <Section title="Exploração Editorial — Produção Real" icon="◻" badge="Séries e campanhas">
          <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>
            Use o projeto <strong style={{ color: "var(--ink)" }}>RC2CLICKS — Exploração Editorial</strong> para produção real.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 14 }}>
            {["série de episódios", "campanha", "posts de autoridade", "carrossel", "stories", "variações", "material para Canva"].map((t) => (
              <Tag key={t} label={t} color="var(--cat-violet)" />
            ))}
          </div>
          <CopyPrompt label="Prompt — Série de 6 Episódios RC2CLICKS" text={PROMPT_SERIE_EPISODIOS} color="var(--cat-violet)" />
        </Section>

        {/* 4. Novo Cliente */}
        <Section title="Criar Identidade Visual para Novo Cliente" icon="◎" badge="4 passos">
          <div style={{ marginBottom: 4 }}>

            <Step num="1" title="Criar novo projeto no Claude Design">
              <div style={{ fontSize: 13, color: "var(--muted)", lineHeight: 1.6 }}>
                Protótipo → Alta fidelidade → Sistema de projeto: <strong style={{ color: "var(--ink)" }}>Nenhum</strong>
                <br />
                Nome: <code style={{ fontSize: 12, background: "var(--line-soft)", padding: "1px 8px", borderRadius: 4, color: GOLD }}>NOME DO CLIENTE — EDITORIAL SYSTEM</code>
                <br />
                <span style={{ fontSize: 12, color: "var(--faint)" }}>Ex: DR GUTEMBERG — MEDICAL EDITORIAL SYSTEM</span>
              </div>
            </Step>

            <Step num="2" title="Criar fundação visual">
              <p style={{ margin: "0 0 4px", fontSize: 13, color: "var(--muted)" }}>
                Gera 4 direções visuais (capa reels, story, post, carrossel, CTA + regras).
              </p>
              <CopyPrompt label="Prompt — Fundação Visual do Cliente" text={PROMPT_CLIENTE_FUNDACAO} />
            </Step>

            <Step num="3" title="Criar Brand Book do cliente">
              <p style={{ margin: "0 0 4px", fontSize: 13, color: "var(--muted)" }}>
                Depois de escolher as direções, peça o Brand Book completo.
              </p>
              <CopyPrompt label="Prompt — Brand Book Editorial" text={PROMPT_CLIENTE_BRANDBOOK} />
            </Step>

            <Step num="4" title="Criar produção real">
              <p style={{ margin: "0 0 4px", fontSize: 13, color: "var(--muted)" }}>
                Com o Brand Book aprovado, inicie a produção de conteúdo.
              </p>
              <CopyPrompt label="Prompt — Produção Real do Cliente" text={PROMPT_CLIENTE_PRODUCAO} />
            </Step>

          </div>
        </Section>

        {/* 5. Fluxo Canva */}
        <Section title="Como usar o Canva depois do Claude Design" icon="◷" badge="Fluxo de produção">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
            <div style={{ background: "var(--line-soft)", borderRadius: 8, padding: "14px 16px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
                Claude Design cria
              </div>
              {["direção", "estrutura", "visual", "referência", "linguagem"].map((i) => (
                <div key={i} style={{ fontSize: 13, color: "var(--muted)", marginBottom: 3 }}>· {i}</div>
              ))}
            </div>
            <div style={{ background: "var(--line-soft)", borderRadius: 8, padding: "14px 16px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--cat-violet)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
                Canva executa
              </div>
              {["arquivo editável", "troca de texto", "ajustes finos", "exportação", "templates reutilizáveis"].map((i) => (
                <div key={i} style={{ fontSize: 13, color: "var(--muted)", marginBottom: 3 }}>· {i}</div>
              ))}
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--faint)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10 }}>
            Fluxo ideal
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {[
              "Gerar no Claude Design",
              "Escolher a melhor direção",
              "Baixar referência em PNG",
              "Recriar no Canva como template editável",
              "Duplicar para produção recorrente",
              "Salvar na pasta do cliente",
            ].map((step, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <div style={{
                  width: 22, height: 22, borderRadius: "50%",
                  background: `${GOLD}15`, color: GOLD,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: 13, color: "var(--muted)" }}>{step}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* 6. Checklist */}
        <Section title="Checklist antes de publicar" icon="✓" badge="Aprovação final">
          <div className="klist">
            <div className="klist-head">
              <MiniBar done={checkedCount} total={CHECKLIST.length} />
              {allChecked
                ? <span className="pill ok"><span className="dot" />Pronto para publicar</span>
                : checkedCount > 0 && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCheckedItems({})}>Limpar</button>}
            </div>
            {CHECKLIST.map((item, i) => (
              <div key={i} className={`klist-row${checkedItems[i] ? " done" : ""}`} style={{ cursor: "pointer" }} onClick={() => toggleCheck(i)}>
                <Check on={!!checkedItems[i]} onClick={() => toggleCheck(i)} label={item} />
                <span className="txt">{item}</span>
              </div>
            ))}
          </div>
        </Section>

      </div>

      {/* Regra principal */}
      <div style={{
        marginTop: 24, padding: "20px 24px",
        background: DARK, borderRadius: "var(--r-card)",
      }}>
        <div style={{ font: "500 11px var(--font-data)", color: "var(--stage-muted)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 10 }}>
          Regra principal
        </div>
        <div style={{ font: "700 20px var(--font-display)", color: "var(--stage-ink)", marginBottom: 8 }}>
          Não criar post isolado.
        </div>
        <div style={{ fontSize: 14, color: "var(--stage-muted)", lineHeight: 1.7 }}>
          Criar sempre dentro de um sistema:
        </div>
        <div style={{
          marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
        }}>
          {["Identidade", "Direções", "Brand Book", "Templates", "Produção"].map((step, i, arr) => (
            <>
              <span key={step} style={{
                fontSize: 12, fontWeight: 700, padding: "4px 14px",
                borderRadius: 20, background: "rgb(255 255 255 / .08)", color: "var(--stage-ink)",
              }}>
                {step}
              </span>
              {i < arr.length - 1 && (
                <span key={`arrow-${i}`} style={{ color: "var(--faint)", fontSize: 16 }}>→</span>
              )}
            </>
          ))}
        </div>
      </div>

    </div>
  );
}
